import React from "react";

import type { PolymorphicBoxProps } from "~/components/polymorphic-box";

const DEFAULT_ELEMENT = "div" satisfies React.ElementType;

export interface ForProps<TValue> {
	/**
	 * The array of items to iterate over and render.
	 */
	each: ReadonlyArray<TValue>;

	/**
	 * Optional placeholder to render when `each` is empty in place of the For element.
	 * Can be any valid React node,such as a string, element, or fragment.
	 * If not provided and `each` is empty, nothing is rendered.
	 */
	empty?: React.ReactNode;

	/**
	 * A render function called for each item in `each`.
	 * It fallbacks the key={index} prop to each child element.
	 *
	 * @param value The current item being rendered.
	 * @param index The index of the current item in the array.
	 * @param arr The full array being rendered.
	 *
	 * @returns A ReactElement for the current item.
	 */
	children: (value: TValue, index: number, arr: ReadonlyArray<TValue>) => React.ReactElement;
}

/**
 * The <For> component is used to render a list of items. It is similar to the .map() function in JavaScript.
 *
 * Inspired by SolidJS's <For> component: {@link https://docs.solidjs.com/reference/components/for}
 */
export const For = React.forwardRef(
	(
		{
			el: Element = DEFAULT_ELEMENT,
			each,
			empty,
			children,
			...props
		}: PolymorphicBoxProps<React.ElementType, ForProps<unknown>>,
		ref,
	) => {
		if (each.length === 0) {
			return empty;
		}

		return (
			<Element ref={ref} {...props}>
				{each.map((value, index) => {
					const child = children(value, index, each);

					return React.cloneElement(child, { key: child.key ?? index });
				})}
			</Element>
		);
	},
) as {
	<TValue, TElement extends React.ElementType = typeof DEFAULT_ELEMENT>(
		props: PolymorphicBoxProps<TElement, ForProps<TValue>>,
	): React.ReactElement;

	displayName?: string;
};

if (import.meta.env.DEV) {
	For.displayName = "For";
}
